import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { hubKeys } from "../../api/hub/client";
import {
  listProjects,
  createProject,
  updateProject,
  adoptProject,
  addMilestone,
  deleteMilestone,
  addProjectUpdate,
  listTasks,
} from "../../api/hub/work";
import type { Project, Task, Milestone } from "../../api/hub/types";
export const projectStatus = {
  planned: "计划中",
  active: "进行中",
  paused: "暂停",
  completed: "已完成",
  canceled: "已取消",
};
export const projectHealth = {
  on_track: "正常",
  at_risk: "有风险",
  off_track: "偏离",
};
export function projectProgress(tasks: Task[]) {
  const counted = tasks.filter((t) => t.status !== "canceled");
  return counted.length
    ? Math.round(
        (counted.reduce(
          (n, t) =>
            n +
            (t.status === "done"
              ? 100
              : ["in_progress", "in_review"].includes(t.status)
                ? 25
                : 0),
          0,
        ) /
          counted.length) *
          100,
      ) / 100
    : 0;
}
export function useProjects() {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: hubKeys.all });
  return {
    projects: useQuery({ queryKey: hubKeys.projects, queryFn: listProjects }),
    tasks: useQuery({ queryKey: [...hubKeys.tasks, "include-triage"], queryFn: () => listTasks(true) }),
    create: useMutation({ mutationFn: createProject, onSuccess: refresh }),
    update: useMutation({
      mutationFn: ({ id, patch }: { id: string; patch: Partial<Project> }) =>
        updateProject(id, patch),
      onSuccess: refresh,
    }),
    adopt: useMutation({
      mutationFn: ({ guid, title }: { guid: string; title: string }) =>
        adoptProject(guid, title),
      onSuccess: refresh,
    }),
    milestone: useMutation({
      mutationFn: ({
        id,
        milestone,
      }: {
        id: string;
        milestone: Partial<Milestone>;
      }) => addMilestone(id, milestone),
      onSuccess: refresh,
    }),
    removeMilestone: useMutation({
      mutationFn: ({ id, milestoneId }: { id: string; milestoneId: string }) => deleteMilestone(id, milestoneId),
      onSuccess: refresh,
    }),
    post: useMutation({
      mutationFn: ({
        id,
        content,
        health,
        updateId,
      }: {
        id: string;
        content: string;
        health: Project["health"];
        updateId?: string;
      }) => addProjectUpdate(id, content, health, updateId),
      onSuccess: refresh,
    }),
  };
}
